/**
 * useAssignRole Hook
 *
 * React Query mutation for assigning global roles to users or groups.
 * Supports multiple assignments in a single operation.
 * Invalidates role, user, and group caches on success.
 * Refreshes current user data if the logged-in user was assigned.
 *
 * Global Roles Architecture:
 * - Roles are tenant-wide (not application-scoped)
 * - Invalidates all roles queries for the tenant on success
 */

import { useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import { roleService } from '../../../api/role.service';
import { userService } from '../../../api/user.service';
import { useAuth } from '../../../contexts/AuthContext';
import type { RoleSanitized, RoleAssignDTO } from '../../../types/role.types';

interface AssignRoleVariables {
  roleId: string;
  data: RoleAssignDTO;
}

/**
 * Assign role mutation
 *
 * @param tenantId - Tenant ID
 * @returns Mutation result
 */
export const useAssignRole = (
  tenantId: string
): UseMutationResult<RoleSanitized, Error, AssignRoleVariables> => {
  const queryClient = useQueryClient();
  const { user, updateUser } = useAuth();

  return useMutation<RoleSanitized, Error, AssignRoleVariables>({
    mutationFn: ({ roleId, data }) => roleService.assignRole(tenantId, roleId, data),
    onSuccess: async (_, variables) => {
      // Invalidate specific role cache
      queryClient.invalidateQueries({ queryKey: ['role', tenantId, variables.roleId] });
      // Invalidate all roles queries for this tenant
      queryClient.invalidateQueries({ queryKey: ['roles', tenantId] });

      // Invalidate affected users
      if (variables.data.users && variables.data.users.length > 0) {
        variables.data.users.forEach((userId) => {
          queryClient.invalidateQueries({ queryKey: ['user', tenantId, userId] });
        });
        queryClient.invalidateQueries({ queryKey: ['users', tenantId] });

        // If the current user was assigned, refresh their data to update role selector
        if (user && variables.data.users.includes(user.userId)) {
          try {
            const freshUserData = await userService.getUser(tenantId, user.userId);
            updateUser(freshUserData);
          } catch (error) {
            console.error('Failed to refresh current user data:', error);
          }
        }
      }

      // Invalidate affected groups
      if (variables.data.groups && variables.data.groups.length > 0) {
        variables.data.groups.forEach((groupId) => {
          queryClient.invalidateQueries({ queryKey: ['group', tenantId, groupId] });
        });
        queryClient.invalidateQueries({ queryKey: ['groups', tenantId] });
      }

      // Invalidate affected applications (bidirectional sync: role.applications <-> application.role)
      if (variables.data.applicationIds && variables.data.applicationIds.length > 0) {
        variables.data.applicationIds.forEach((applicationId) => {
          queryClient.invalidateQueries({ queryKey: ['applications', tenantId, applicationId] });
        });
        queryClient.invalidateQueries({ queryKey: ['applications', tenantId] });
      }

      // Invalidate affected service accounts (bidirectional sync: role.serviceAccounts <-> serviceAccount.roles)
      if (variables.data.serviceAccountIds && variables.data.serviceAccountIds.length > 0) {
        variables.data.serviceAccountIds.forEach((serviceAccountId) => {
          queryClient.invalidateQueries({ queryKey: ['serviceAccount', tenantId, serviceAccountId] });
        });
        queryClient.invalidateQueries({ queryKey: ['serviceAccounts', tenantId] });
      }
    },
    onError: (error) => {
      console.error('Failed to assign role:', error);
    },
  });
};
